import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  Search, Beaker, Users, FileText, MessageSquare, GraduationCap,
  Code, Key, Database, Terminal, Shield, Layers,
  Compass, PenTool, Target, BarChart3, Globe, TrendingUp,
  Sparkles, BookOpen, ArrowLeft, ArrowRight, Clock, CheckCircle2,
  Hash
} from 'lucide-react';
import ResourcesLayout from './ResourcesLayout';
import { ALL_GUIDES } from './guideContent';
import './GuideDetailPage.css';

const ICON_MAP = {
  Search, Beaker, Users, FileText, MessageSquare, GraduationCap,
  Code, Key, Database, Terminal, Shield, Layers,
  Compass, PenTool, Target, BarChart3, Globe, TrendingUp,
  Sparkles, BookOpen,
};

const GuideDetailPage = ({ type = 'doc' }) => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const guide = ALL_GUIDES[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!guide) {
    return (
      <ResourcesLayout>
        <div className="guide-not-found">
          <BookOpen size={48} />
          <h2>Guide Not Found</h2>
          <p>The guide you are looking for does not exist or has been moved.</p>
          <Link to={type === 'doc' ? '/docs' : '/guides'} className="guide-back-link">
            <ArrowLeft size={16} />
            Back to {type === 'doc' ? 'Documentation' : 'Research Guides'}
          </Link>
        </div>
      </ResourcesLayout>
    );
  }

  const IconComponent = ICON_MAP[guide.icon] || BookOpen;
  const backPath = type === 'doc' ? '/docs' : '/guides';
  const backLabel = type === 'doc' ? 'Documentation' : 'Research Guides';

  // Resolve related guides
  const relatedGuides = (guide.relatedSlugs || [])
    .map((s) => ALL_GUIDES[s] ? { slug: s, ...ALL_GUIDES[s] } : null)
    .filter(Boolean);

  return (
    <ResourcesLayout>
      <article className="guide-detail">
        {/* Back navigation */}
        <Link to={backPath} className="guide-back-link">
          <ArrowLeft size={16} />
          {backLabel}
        </Link>

        {/* Header */}
        <header className="guide-header">
          <div className="guide-header-top">
            <div className={`guide-header-icon ${guide.tagColor ? `guide-header-icon--${guide.tagColor}` : ''}`}>
              <IconComponent size={24} />
            </div>
            {guide.tag && (
              <span className={`guide-tag guide-tag--${guide.tagColor}`}>{guide.tag}</span>
            )}
          </div>
          <h1 className="guide-title">{guide.title}</h1>
          <p className="guide-description">{guide.description}</p>
          <div className="guide-meta">
            <span className="guide-meta-item">
              <Clock size={14} />
              {guide.readTime}
            </span>
            {guide.difficulty && (
              <span className={`guide-difficulty guide-difficulty--${guide.difficulty.toLowerCase()}`}>
                {guide.difficulty}
              </span>
            )}
            {guide.lastUpdated && (
              <span className="guide-meta-item">
                Updated {guide.lastUpdated}
              </span>
            )}
          </div>
        </header>

        {/* Table of Contents */}
        <nav className="guide-toc">
          <h3 className="guide-toc-title">
            <Hash size={14} />
            In This Guide
          </h3>
          <ul className="guide-toc-list">
            {guide.sections.map((section) => (
              <li key={section.id}>
                <a href={`#${section.id}`} className="guide-toc-link">
                  {section.title}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        {/* Content Sections */}
        <div className="guide-content">
          {guide.sections.map((section) => (
            <section key={section.id} id={section.id} className="guide-section">
              <h2 className="guide-section-title">{section.title}</h2>
              {section.paragraphs.map((para, i) => (
                <p key={i} className="guide-section-text">{para}</p>
              ))}
            </section>
          ))}
        </div>

        {/* Key Takeaways */}
        {guide.keyTakeaways?.length > 0 && (
          <aside className="guide-takeaways">
            <h3 className="guide-takeaways-title">
              <CheckCircle2 size={18} />
              Key Takeaways
            </h3>
            <ul className="guide-takeaways-list">
              {guide.keyTakeaways.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </aside>
        )}

        {/* Related Guides */}
        {relatedGuides.length > 0 && (
          <nav className="guide-related">
            <h3 className="guide-related-title">Related Guides</h3>
            <div className="guide-related-grid">
              {relatedGuides.map((related) => {
                const RelIcon = ICON_MAP[related.icon] || BookOpen;
                const linkBase = related.slug in (
                  // doc slugs link to /docs/, research slugs to /guides/
                  ['paper-search-discovery','research-labs','communities','research-feed',
                   'messaging-e2ee','courses-workshops','api-overview','authentication',
                   'endpoints-reference','websocket-events','encryption-security','webhooks-integrations']
                  .reduce((acc, s) => ({ ...acc, [s]: true }), {})
                ) ? '/docs' : '/guides';
                return (
                  <Link key={related.slug} to={`${linkBase}/${related.slug}`} className="guide-related-card">
                    <div className="guide-related-icon">
                      <RelIcon size={16} />
                    </div>
                    <div>
                      <span className="guide-related-card-title">{related.title}</span>
                      <span className="guide-related-card-time">{related.readTime}</span>
                    </div>
                    <ArrowRight size={14} className="guide-related-arrow" />
                  </Link>
                );
              })}
            </div>
          </nav>
        )}
      </article>
    </ResourcesLayout>
  );
};

export default GuideDetailPage;
